import { supabase } from './supabase';

/**
 * Keys used inside the public.cms_content table.
 * Publicly readable: page_content, resources, hero_messages, careers, content,
 * blog_posts. Admin-only readable: settings, resources_drafts, blog_posts_drafts
 * (and the insights_* keys below).
 */
export const CMS_KEYS = {
  content: 'content',
  pageContent: 'page_content',
  resources: 'resources',
  resourcesDrafts: 'resources_drafts',
  blogPosts: 'blog_posts',
  blogPostsDrafts: 'blog_posts_drafts',
  settings: 'settings',
  heroMessages: 'hero_messages',
  careers: 'careers',
} as const;

/** Admin-only keys for the insights tools (no public read policy — correct by design). */
export const CMS_INSIGHTS_KEYS = {
  calendar: 'insights_calendar',
  competitors: 'insights_competitors',
  scanResults: 'insights_scan_results',
  prompts: 'insights_prompts',
} as const;

/** Returns the signed-in admin email, or null for anonymous visitors. */
export async function getAdminEmail(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.user?.email ?? null;
  } catch {
    return null;
  }
}

/** Fetches the rows for the requested keys. Missing keys are simply absent from the result. */
export async function fetchCmsKeys(keys: string[]): Promise<Record<string, unknown>> {
  const { data, error } = await supabase
    .from('cms_content')
    .select('key,value')
    .in('key', keys);
  if (error) throw error;
  const result: Record<string, unknown> = {};
  (data || []).forEach((row: { key: string; value: unknown }) => {
    result[row.key] = row.value;
  });
  return result;
}

/** Upserts a single CMS block and stamps who changed it. */
export async function upsertCmsKey(
  key: string,
  value: unknown,
  updatedBy: string | null,
): Promise<void> {
  const { error } = await supabase
    .from('cms_content')
    .upsert(
      { key, value, updated_by: updatedBy, updated_at: new Date().toISOString() },
      { onConflict: 'key' },
    );
  if (error) throw error;
}